/**
 * recomputeDerivation.ts
 *
 * Whole-graph propagation pass. Walks the bipartite DAG and rebuilds, for every
 * material:
 *   - `composition.directContributions` — the immediate upstream sources, and
 *   - `composition.constituents` — the transitive set of root (entry-point)
 *     materials physically present in the node. e.g. m1→m2 makes m2's
 *     constituents {m1, m2}; if m2 later donates to m7, m7's become {m1, m2, m7}.
 *
 * Both are derived from the CURRENT state, so an upstream edit (e.g. renaming a
 * source material) reflects on all downstream materials.
 *
 * Pure and side-effect free: takes a `graphData` and returns a new one with
 * refreshed material compositions. Other composition fields (`finalQuantity`)
 * and all operations/edges/groups are preserved untouched.
 *
 * The structural `lineage` field is intentionally NOT computed here — it is
 * derived authoritatively by the v2 backend from the same topology on save.
 */

import type { GraphEditorState } from './types';
import type {
  MaterialNodeSpec,
  OperationNodeSpec,
  Composition,
  CompositionConstituent,
  DirectContribution,
} from '../graph/domain/types';
import {
  computeInitialComposition,
  propagateTransferComposition,
  propagateComplexComposition,
  propagateSimpleComposition,
} from './compositionService';

type GraphData = GraphEditorState['graphData'];

export function recomputeDerivation(graphData: GraphData): GraphData {
  const { materialNodes, operationNodes, flowEdges } = graphData;

  // Build op→inputs, op→outputs, material→producing-op maps from the flow edges.
  const opInputs = new Map<string, string[]>();
  const producerOfMaterial = new Map<string, string>();

  for (const edge of Object.values(flowEdges)) {
    const { sourceId, targetId } = edge;
    if (operationNodes[targetId] && materialNodes[sourceId]) {
      // material → operation (an input to the op)
      const arr = opInputs.get(targetId) ?? [];
      arr.push(sourceId);
      opInputs.set(targetId, arr);
    }
    if (operationNodes[sourceId] && materialNodes[targetId]) {
      // operation → material (an output; that op produced this material)
      producerOfMaterial.set(targetId, sourceId);
    }
  }

  const nameOf = (id?: string): string | undefined =>
    id ? materialNodes[id]?.name : undefined;

  // ── Pass 1: rebuild each material's immediate directContributions ──────────
  const contribsById = new Map<string, DirectContribution[]>();
  for (const [id, mat] of Object.entries(materialNodes)) {
    const producerId = producerOfMaterial.get(id);
    contribsById.set(
      id,
      producerId
        ? contributionsForProducedMaterial(
            id,
            operationNodes[producerId],
            opInputs.get(producerId) ?? [],
            nameOf,
          )
        : computeInitialComposition(id, mat.name).directContributions!,
    );
  }

  // ── Pass 2: accumulate the transitive set of source-material constituents ──
  // A material physically contains every root (entry-point) material that has
  // flowed into it. e.g. m1→m2 gives m2 = {m1, m2}; m2→m7 gives m7 = {m1, m2, m7}.
  const constituentsById = new Map<string, CompositionConstituent[]>();
  const visiting = new Set<string>();

  const constituentOf = (matId: string): CompositionConstituent => {
    const mat = materialNodes[matId];
    return {
      sourceNodeId: matId,
      materialName: mat?.name ?? '',
      containerName: mat?.container?.name,
    };
  };

  const constituentsOf = (matId: string): CompositionConstituent[] => {
    const cached = constituentsById.get(matId);
    if (cached) return cached;
    if (visiting.has(matId)) return []; // cycle guard
    visiting.add(matId);

    const contribs = contribsById.get(matId) ?? [];
    const isRoot = contribs.length === 0 || contribs.some((c) => c.contributionType === 'initial');

    let result: CompositionConstituent[];
    if (isRoot) {
      // An entry-point material contributes only itself.
      result = [constituentOf(matId)];
    } else {
      // A produced material is composed of the roots of everything upstream.
      const byRoot = new Map<string, CompositionConstituent>();
      for (const c of contribs) {
        if (!c.fromNodeId) continue;
        for (const rc of constituentsOf(c.fromNodeId)) {
          byRoot.set(rc.sourceNodeId, rc);
        }
      }
      result = [...byRoot.values()];
    }

    visiting.delete(matId);
    constituentsById.set(matId, result);
    return result;
  };

  // ── Assemble the new material specs ───────────────────────────────────────
  const nextMaterials: Record<string, MaterialNodeSpec> = {};
  for (const [id, mat] of Object.entries(materialNodes)) {
    const composition: Composition = {
      ...(mat.composition ?? {}),
      directContributions: contribsById.get(id),
      constituents: constituentsOf(id),
    };
    nextMaterials[id] = { ...mat, composition };
  }

  const result = { ...graphData, materialNodes: nextMaterials };
  
  return result;
}

/**
 * Rebuild the directContributions for a material produced by an operation,
 * dispatching on the operation's archetype and pulling upstream names live.
 */
function contributionsForProducedMaterial(
  materialId: string,
  op: OperationNodeSpec | undefined,
  inputIds: string[],
  nameOf: (id?: string) => string | undefined,
): DirectContribution[] {
  const opId = op?.id ?? '';

  if (op?.archetype === 'TRANSFER' && op.transferRow) {
    const tr = op.transferRow;
    const sourceId = tr.sourceMaterialId ?? inputIds[0];
    // Destination pre-state = the op input that isn't the source (falls back to
    // the transferRow's destination when there is no pre-existing dest input).
    const destId = inputIds.find((i) => i !== sourceId) ?? tr.destinationMaterialId;
    const result = propagateTransferComposition(
      sourceId ?? '',
      nameOf(sourceId) ?? '',
      destId ?? '',
      nameOf(destId),
      opId,
      tr.quantity,
    );
    // Classify this output as the merged (destination) post-state vs the source
    // leftover. Some creation paths populate only ONE of the two post ids, so
    // fall back progressively: dest id → "not the source post" → name match.
    const isDestPost =
      tr.destinationPostMaterialId != null
        ? materialId === tr.destinationPostMaterialId
        : tr.sourcePostMaterialId != null
          ? materialId !== tr.sourcePostMaterialId
          : nameOf(materialId) === nameOf(destId) && nameOf(materialId) !== nameOf(sourceId);
    // The merged (destination post-state) tracks both source + destination;
    // the remainder (source post-state) tracks only the source.
    return isDestPost
      ? result.destPostComposition.directContributions!
      : result.sourcePostComposition.directContributions!;
  }

  if (op?.archetype === 'COMPLEX') {
    // Refresh names for each contributing input.
    return propagateComplexComposition(inputIds, opId).directContributions!.map((c) => ({
      ...c,
      fromNodeName: nameOf(c.fromNodeId) ?? c.fromNodeName,
    }));
  }

  // TRANSFORM / MEASURE / any single-input simple operation.
  const sourceId = inputIds[0];
  return propagateSimpleComposition(sourceId ?? '', nameOf(sourceId) ?? '', opId)
    .directContributions!;
}
